import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { ApiService, Funcionario } from '../../api.service';
import { ServicoFormatacao } from '../../shared/servico-formatacao';

@Injectable({ providedIn: 'root' })
export class ServicoFuncionarioAdministrativo {
  constructor(
    private readonly api: ApiService,
    private readonly servicoFormatacao: ServicoFormatacao
  ) {}

  novoFuncionario(empregadorId = ''): Funcionario {
    return {
      nome: '',
      matricula: '',
      cpf: '',
      pisPasep: '',
      imagemFacialBase64: '',
      departamentoId: '',
      empregadorId,
      senhaPortal: '',
      ativo: true,
      podeSolicitarOficio: false
    };
  }

  prepararEdicao(funcionario: Funcionario, empregadorId: string): Funcionario {
    return {
      ...funcionario,
      empregadorId: funcionario.empregador?.id || funcionario.empregadorId || empregadorId,
      departamentoId: funcionario.departamentoId || '',
      departamento: funcionario.departamentoNome || funcionario.departamento || '',
      cpf: this.servicoFormatacao.formatarCpf(funcionario.cpf),
      imagemFacialBase64: funcionario.imagemFacialBase64 || '',
      faces: funcionario.faces || [],
      descritorFacial: [],
      senhaPortal: '',
      podeSolicitarOficio: Boolean(funcionario.podeSolicitarOficio)
    };
  }

  prepararSalvar(funcionario: Funcionario, empregadorId: string): Funcionario {
    const cpf = this.servicoFormatacao.apenasNumeros(funcionario.cpf);
    return {
      ...funcionario,
      empregadorId,
      departamentoId: funcionario.departamentoId || undefined,
      cpf,
      senhaPortal: funcionario.senhaPortal?.trim() || undefined,
      imagemFacialMimeType: funcionario.imagemFacialBase64 ? 'image/jpeg' : funcionario.imagemFacialMimeType
    };
  }

  selecionarFuncionarioCadastro(funcionarios: Funcionario[], funcionarioSelecionado = '') {
    if (funcionarioSelecionado && funcionarios.some(item => item.id === funcionarioSelecionado)) {
      return funcionarioSelecionado;
    }

    return funcionarios[0]?.id || '';
  }

  cpfValido(funcionario: Funcionario) {
    return this.servicoFormatacao.apenasNumeros(funcionario.cpf).length === 11;
  }

  mensagemFuncionarioCarregado(funcionario: Funcionario) {
    return `Funcionario carregado para edicao: ${funcionario.nome}.`;
  }

  mensagemFuncionarioSalvo(_funcionario: Funcionario, editando = false) {
    return editando
      ? 'Sucesso.\nRegistro alterado com sucesso.'
      : 'Sucesso.\nRegistro salvo com sucesso.';
  }

  mensagemCpfInvalido() {
    return 'CPF deve conter 11 digitos.';
  }

  mensagemErroCarregar() {
    return 'Erro ao carregar funcionario para edicao.';
  }

  mensagemErroSalvar() {
    return 'Erro ao salvar funcionario. Confira empregador, nome, matricula e CPF.';
  }

  mensagemFuncionarioExcluido() {
    return 'Sucesso.\nRegistro excluído com sucesso.';
  }

  mensagemErroExcluir() {
    return 'Erro ao excluir funcionario.';
  }

  listar(empregadorId?: string): Observable<Funcionario[]> {
    return this.api.listarFuncionarios(empregadorId);
  }

  listarPorEmpregadorSelecionado(empregadorId: string): Observable<Funcionario[]> {
    if (!empregadorId) {
      return of([]);
    }

    return this.listar(empregadorId);
  }

  carregarFuncionarioCadastro(
    funcionarios: Funcionario[],
    funcionarioSelecionado: string,
    empregadorId: string
  ): Observable<Funcionario | null> {
    const selecionado = funcionarios.find(item => item.id === funcionarioSelecionado);
    if (!selecionado?.id) {
      return of(null);
    }

    return this.buscar(selecionado.id).pipe(
      map(funcionario => this.prepararEdicao(funcionario, empregadorId))
    );
  }

  buscar(funcionarioId: string): Observable<Funcionario> {
    return this.api.buscarFuncionario(funcionarioId);
  }

  salvar(funcionario: Funcionario): Observable<Funcionario> {
    return this.api.salvarFuncionario(funcionario);
  }

  excluir(funcionarioId: string): Observable<void> {
    return this.api.excluirFuncionario(funcionarioId);
  }

  validarFace(funcionarioId: string, descritorFacial: number[]): Observable<void> {
    return this.api.validarFaceFuncionario(funcionarioId, descritorFacial);
  }

  cadastrarFace(funcionarioId: string, imagemFacialBase64: string, descritorFacial: number[]): Observable<Funcionario> {
    return this.api.cadastrarFaceFuncionario(funcionarioId, imagemFacialBase64, 'image/jpeg', descritorFacial);
  }

  removerFace(funcionarioId: string, faceId: string): Observable<Funcionario> {
    return this.api.removerFaceFuncionario(funcionarioId, faceId);
  }
}
